/**
 * Structured data builders for SEO and agent discoverability
 */

import type { Product } from '../types';

import { SITE_DESCRIPTION, SITE_EMAIL, SITE_NAME, SITE_ORIGIN } from './site';

export const getFirstStlLink = (product: Product): string | undefined => {
  if (!product.stlLinks) {
    return undefined;
  }

  return (
    product.stlLinks.myminifactory ||
    product.stlLinks.cults ||
    product.stlLinks.makerworld ||
    product.stlLinks.thingiverse
  );
};

export const buildOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/favicon.png`,
  email: SITE_EMAIL,
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Melbourne',
    addressRegion: 'VIC',
    addressCountry: 'AU',
  },
  sameAs: [
    'https://www.myminifactory.com/users/FaeForest',
    'https://cults3d.com/@FaeForest',
  ],
});

export const buildWebSiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_ORIGIN,
  description: SITE_DESCRIPTION,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_ORIGIN}/products?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const buildItemListJsonLd = (products: Product[]) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Products',
  url: `${SITE_ORIGIN}/products`,
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${SITE_ORIGIN}/products/${product.id}`,
      name: product.name,
    })),
  },
});

export const buildFaqPageJsonLd = (items: Array<{ q: string; a: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: a,
    },
  })),
});

export const buildBreadcrumbJsonLd = (product: Product) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_ORIGIN,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Products',
      item: `${SITE_ORIGIN}/products`,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: product.category,
      item: `${SITE_ORIGIN}/products?category=${encodeURIComponent(product.category)}`,
    },
    {
      '@type': 'ListItem',
      position: 4,
      name: product.name,
      item: `${SITE_ORIGIN}/products/${product.id}`,
    },
  ],
});

export const buildProductJsonLd = (product: Product, displayPrice: number) => {
  const productUrl = `${SITE_ORIGIN}/products/${product.id}`;
  const offerUrl = getFirstStlLink(product) ?? productUrl;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.images,
    category: product.category,
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    ...(product.rating &&
      product.reviews && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: product.rating,
          reviewCount: product.reviews,
        },
      }),
    offers: {
      '@type': 'Offer',
      price: displayPrice,
      priceCurrency: 'AUD',
      availability: product.inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      url: offerUrl,
    },
  };
};
